import { LocalStorageManager } from '../services/localStorage';

export enum ErrorCategory {
  GROUP_CONTEXT = 'GROUP_CONTEXT',
  AUTHENTICATION = 'AUTHENTICATION',
  NETWORK = 'NETWORK',
  PERMISSION = 'PERMISSION',
  VALIDATION = 'VALIDATION',
  NAVIGATION = 'NAVIGATION',
  APPLICATION = 'APPLICATION',
}

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: number;
  groupSlug?: string;
  timestamp: number;
}

export interface ErrorReport {
  error: Error;
  context: ErrorContext;
  userAgent: string;
  url: string;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: ErrorReport[] = [];
  private isReporting = false;

  private constructor() {}

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle and categorize errors for appropriate response
   */
  public handleError(error: Error, context: Partial<ErrorContext> = {}): ErrorCategory {
    const fullContext: ErrorContext = {
      timestamp: Date.now(),
      ...context,
    };

    // Log error for debugging
    console.error('ErrorHandler:', error, fullContext);

    // Queue error for reporting (in production)
    if (process.env.NODE_ENV === 'production') {
      this.queueErrorReport(error, fullContext);
    }

    // Categorize the error
    return this.categorizeError(error);
  }

  /**
   * Categorize errors to determine appropriate handling strategy
   */
  public categorizeError(error: Error): ErrorCategory {
    // Group context errors
    if (this.isGroupContextError(error)) {
      return ErrorCategory.GROUP_CONTEXT;
    }

    // Authentication errors
    if (this.isAuthError(error)) {
      return ErrorCategory.AUTHENTICATION;
    }

    // Network/API errors
    if (this.isNetworkError(error)) {
      return ErrorCategory.NETWORK;
    }

    // Permission errors
    if (this.isPermissionError(error)) {
      return ErrorCategory.PERMISSION;
    }

    // Validation errors
    if (this.isValidationError(error)) {
      return ErrorCategory.VALIDATION;
    }

    // Navigation errors
    if (this.isNavigationError(error)) {
      return ErrorCategory.NAVIGATION;
    }

    // Default to application error
    return ErrorCategory.APPLICATION;
  }

  /**
   * Handle group context errors specifically
   */
  public handleGroupContextError(error: Error, context: Partial<ErrorContext> = {}): void {
    console.warn('Group context error detected:', error.message);

    try {
      // Clear potentially invalid group data
      LocalStorageManager.clearGroupPreference();
      
      // Store error message for group selection page
      sessionStorage.setItem('groupSelectionError', 'Si è verificato un problema con il contesto del gruppo');
      
      // Redirect to group selection if not already there (but not from public pages)
      const currentPath = globalThis.location.pathname;
      const { isPublicRoute } = require('../constants/routes');
      
      if (!isPublicRoute(currentPath)) {
        globalThis.location.replace('/group-selection');
      }
    } catch (cleanupError) {
      console.error('Failed to cleanup after group context error:', cleanupError);
    }

    this.handleError(error, { ...context, component: 'GroupContext' });
  }

  /**
   * Handle authentication errors
   */
  public async handleAuthError(error: Error, context: Partial<ErrorContext> = {}): Promise<void> {
    // console.warn('Authentication error detected:', error.message);

    try {
      // Don't redirect or clear data on public pages
      const currentPath = globalThis.location.pathname;
      const { isPublicRoute } = await import('../constants/routes');
      
      if (isPublicRoute(currentPath)) {
        // console.log('Auth error on public route, ignoring:', currentPath);
        return;
      }
      
      // Note: User notifications are handled by the calling component (useApiError)
      
      // Clear auth-related data
      LocalStorageManager.clearGroupPreference();
      
      // Redirect to login after a short delay to let user see the message
      setTimeout(() => {
        if (!currentPath.includes('/login')) {
          globalThis.location.replace('/login');
        }
      }, 2000);
      
    } catch (cleanupError) {
      console.error('Failed to cleanup after auth error:', cleanupError);
    }

    this.handleError(error, { ...context, component: 'Authentication' });
  }

  /**
   * Get user-friendly error message
   */
  public getUserFriendlyMessage(error: Error): string {
    const category = this.categorizeError(error);

    switch (category) {
      case ErrorCategory.GROUP_CONTEXT:
        return 'Si è verificato un problema con il contesto del gruppo. Verrai reindirizzato alla selezione del gruppo.';
      
      case ErrorCategory.AUTHENTICATION:
        return 'La tua sessione è scaduta. Effettua nuovamente il login.';
      
      case ErrorCategory.NETWORK:
        return 'Problema di connessione. Controlla la tua connessione internet e riprova.';
      
      case ErrorCategory.PERMISSION:
        return 'Non hai i permessi necessari per questa operazione.';
      
      case ErrorCategory.VALIDATION:
        return 'I dati inseriti non sono validi. Controlla e riprova.';
      
      case ErrorCategory.NAVIGATION:
        return 'Errore di navigazione. Prova a ricaricare la pagina.';
      
      default:
        return 'Si è verificato un errore imprevisto. Riprova o contatta il supporto se il problema persiste.';
    }
  }

  /**
   * Check if error should trigger automatic retry
   */
  public shouldAutoRetry(error: Error): boolean {
    const category = this.categorizeError(error);
    
    // Only retry network errors and some application errors
    return category === ErrorCategory.NETWORK || 
           (category === ErrorCategory.APPLICATION && this.isTransientError(error));
  }

  private isGroupContextError(error: Error): boolean {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';
    
    const patterns = [
      'group context', 'group slug', 'gruppo non trovato', 'non hai accesso a questo gruppo',
      'contesto del gruppo', 'invalid group', 'group not found', 'unauthorized group access',
      'missing group context', 'x-group-slug'
    ];

    return patterns.some(pattern => message.includes(pattern) || stack.includes(pattern));
  }

  private isAuthError(error: Error): boolean {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';
    
    const patterns = [
      'unauthorized', 'authentication', 'login', 'session', 'token',
      'not authenticated', 'auth failed', 'invalid credentials'
    ];

    return patterns.some(pattern => message.includes(pattern) || stack.includes(pattern));
  }

  private isNetworkError(error: Error): boolean {
    const message = error.message.toLowerCase();
    
    const patterns = [
      'network error', 'fetch error', 'connection failed', 'timeout',
      'service unavailable', 'server error', 'bad gateway', 'gateway timeout',
      'connection reset', 'connection refused', 'dns', 'cors'
    ];

    return patterns.some(pattern => message.includes(pattern));
  }

  private isPermissionError(error: Error): boolean {
    const message = error.message.toLowerCase();
    
    const patterns = [
      'forbidden', 'permission denied', 'access denied', 'not allowed',
      'insufficient privileges', 'unauthorized access'
    ];

    return patterns.some(pattern => message.includes(pattern));
  }

  private isValidationError(error: Error): boolean {
    const message = error.message.toLowerCase();
    
    const patterns = [
      'validation', 'invalid', 'required', 'format', 'schema',
      'bad request', 'malformed', 'missing parameter'
    ];

    return patterns.some(pattern => message.includes(pattern));
  }

  private isNavigationError(error: Error): boolean {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';
    
    const patterns = [
      'navigation', 'router', 'route', 'history', 'location',
      'redirect', 'navigate', 'link'
    ];

    return patterns.some(pattern => message.includes(pattern) || stack.includes(pattern));
  }

  private isTransientError(error: Error): boolean {
    const message = error.message.toLowerCase();
    
    const patterns = [
      'temporary', 'timeout', 'busy', 'overloaded', 'rate limit',
      'try again', 'retry', 'service unavailable'
    ];

    return patterns.some(pattern => message.includes(pattern));
  }

  private queueErrorReport(error: Error, context: ErrorContext): void {
    const report: ErrorReport = {
      error,
      context,
      userAgent: navigator.userAgent,
      url: globalThis.location.href,
    };

    this.errorQueue.push(report);

    // Process queue if not already processing
    if (!this.isReporting) {
      this.processErrorQueue();
    }
  }

  private async processErrorQueue(): Promise<void> {
    if (this.errorQueue.length === 0 || this.isReporting) {
      return;
    }

    this.isReporting = true;

    try {
      // In a real implementation, you would send errors to a logging service
      // For now, we'll just log them
      while (this.errorQueue.length > 0) {
        const report = this.errorQueue.shift();
        if (report) {
          console.log('Error report:', {
            message: report.error.message,
            stack: report.error.stack,
            context: report.context,
            userAgent: report.userAgent,
            url: report.url,
          });
        }
      }
    } catch (reportingError) {
      console.error('Failed to process error queue:', reportingError);
    } finally {
      this.isReporting = false;
    }
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();